import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { useAppSelector } from 'app/hooks';
import DuctRunSuppliesGroup from 'components/systems/duct-run-supplies-group';
import { SystemOptionZoneType } from 'data/types';
import { useEffect, useMemo, useState } from 'react';
import {
  DUCT_NOT_QUOTED_NOTICE,
  DUCT_SOURCING_NOTICE,
  annotateSuppliesWithStock,
  resolveZoneDuctingPlan
} from 'utils/duct-component-fallback';
import { plenumNeedForFit } from 'utils/concealed-plenum-dimensions';
import { buildZoneDuctSupplies } from 'utils/duct-supplies';
import {
  QuotedAccessoryLiteType,
  deriveNeededZoneDuctingProducts,
  deriveReturnRequirement,
  deriveSupplyRequirement,
  findZoneDuctingEvaluation,
  formatDuctingQuoteNotice,
  getDuctingQuoteStatus,
  getZoneDuctingModes,
  isConcealedDuctProduct,
  resolveZoneDuctModeKey,
  quotedReturnCoverageLabel,
  quotedSupplyCoverageLabel,
  returnKitCoverageLabel,
  supplyKitCoverageLabel
} from 'utils/ducting-products';
import {
  ductSuppliesModeForKey,
  ZONE_DUCT_MODE_LABELS
} from 'utils/zone-duct-modes';

type ZoneDuctingProductsProps = Readonly<{
  configId: string;
  condenserUniqueId: string;
  zone: SystemOptionZoneType;
  quotedAccessories?: QuotedAccessoryLiteType[];
}>;

export default function ZoneDuctingProducts({
  configId,
  condenserUniqueId,
  zone,
  quotedAccessories
}: ZoneDuctingProductsProps) {
  const { ductingKitProducts, ductingComponents } = useAppSelector(
    (state) => state.data
  );
  const { configurations } = useAppSelector((state) => state.configurations);
  const modeToggle = useAppSelector(
    (state) => state.ui.zoneDuctModes[zone.uniqueId] ?? null
  );

  const derived = useMemo(() => {
    const product = zone.selectedProduct;
    if (!product || !isConcealedDuctProduct(product)) return null;
    const validSystems =
      configurations
        .find((config) => config.uniqueId === configId)
        ?.condensers.find(
          (condenser) => condenser.uniqueId === condenserUniqueId
        )?.validSystems ?? [];
    const ducting = findZoneDuctingEvaluation(
      validSystems,
      zone.uniqueId,
      product.productId
    );
    const modes = getZoneDuctingModes(ducting);
    const selectedModeKey = resolveZoneDuctModeKey(modes, modeToggle);
    const selectedEvaluation = selectedModeKey
      ? modes[selectedModeKey] ?? null
      : null;
    const selectedDucting = selectedEvaluation
      ? {
          airHandlerProductId: product.productId,
          metrics: selectedEvaluation.metrics,
          reasons: selectedEvaluation.reasons
        }
      : ducting;
    const suppliesMode = selectedModeKey
      ? ductSuppliesModeForKey(selectedModeKey)
      : 'flex_only';
    const needed = deriveNeededZoneDuctingProducts({
      airHandlerProduct: product,
      ducting: selectedDucting as typeof ducting,
      ductingKitProducts,
      mode: suppliesMode
    });
    const roomNamesByUniqueId = new Map(
      zone.zoneItems.map((item) => [item.structureItemId, item.name])
    );
    const metrics = selectedDucting?.metrics ?? null;
    const plan = resolveZoneDuctingPlan(
      metrics,
      ductingComponents,
      suppliesMode,
      needed
    );
    const supplyRequirement = metrics ? deriveSupplyRequirement(metrics) : null;
    const returnRequirement = metrics
      ? deriveReturnRequirement(metrics, suppliesMode)
      : null;
    const supplyCoveredByLabel = quotedAccessories
      ? quotedSupplyCoverageLabel(
          supplyRequirement,
          quotedAccessories,
          ductingKitProducts
        )
      : supplyKitCoverageLabel(
          plan.supply.source === 'kit' ? needed?.supplyKit ?? null : null
        );
    const returnCoveredByLabel = quotedAccessories
      ? quotedReturnCoverageLabel(
          returnRequirement,
          quotedAccessories,
          ductingKitProducts
        )
      : returnKitCoverageLabel(
          plan.return.source === 'kit' ? needed?.returnKit ?? null : null
        );
    const supplies = metrics
      ? buildZoneDuctSupplies(
          metrics,
          roomNamesByUniqueId,
          {
            supplyCoveredByLabel,
            returnCoveredByLabel
          },
          suppliesMode,
          plenumNeedForFit(needed?.plenumFit, needed?.requiredPlenumDims)
        )
      : null;
    const neededForQuote = needed
      ? {
          ...needed,
          supplyKit: plan.supply.source === 'kit' ? needed.supplyKit : null,
          returnKit: plan.return.source === 'kit' ? needed.returnKit : null
        }
      : null;
    const quoteStatus =
      neededForQuote && quotedAccessories
        ? getDuctingQuoteStatus(
            neededForQuote,
            quotedAccessories,
            ductingKitProducts
          )
        : null;
    const annotated = supplies
      ? annotateSuppliesWithStock(
          supplies,
          ductingComponents,
          quotedAccessories
        )
      : null;
    return {
      modes,
      selectedModeKey,
      supplies: annotated?.supplies ?? supplies,
      missingComponentCount: annotated?.missingCount ?? 0,
      notQuotedCount: annotated?.notQuotedCount ?? 0,
      quoteStatus
    };
  }, [
    configurations,
    configId,
    condenserUniqueId,
    zone,
    ductingKitProducts,
    ductingComponents,
    quotedAccessories,
    modeToggle
  ]);

  const selectedModeKey = derived?.selectedModeKey ?? null;
  const supplies = derived?.supplies ?? null;
  const missingComponentCount = derived?.missingComponentCount ?? 0;
  const notQuotedCount = derived?.notQuotedCount ?? 0;
  const quoteStatus = derived?.quoteStatus ?? null;
  const hasQuoteContext = quotedAccessories !== undefined;
  const quoteNotice = hasQuoteContext
    ? formatDuctingQuoteNotice(quoteStatus)
    : null;
  const [suppliesToggle, setSuppliesToggle] = useState<boolean | null>(null);
  const productId = zone.selectedProduct?.productId;

  useEffect(() => {
    setSuppliesToggle(null);
  }, [zone.uniqueId, productId]);

  if (!supplies) return null;

  const suppliesExpanded = suppliesToggle ?? true;
  const coveredLabel = hasQuoteContext
    ? 'covered by quoted kits'
    : 'covered by kits';
  const suppliesCountLabel =
    supplies && supplies.neededLineCount > 0
      ? `${supplies.neededLineCount} items`
      : coveredLabel;

  return (
    <div className='mt-1 flex flex-col gap-2 rounded-md border border-dashed border-gray-300 bg-gray-50 p-3'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <button
          type='button'
          className='flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-700'
          onClick={(e) => {
            e.stopPropagation();
            setSuppliesToggle(!suppliesExpanded);
          }}
        >
          {suppliesExpanded ? (
            <ChevronDownIcon className='h-4 w-4' />
          ) : (
            <ChevronRightIcon className='h-4 w-4' />
          )}
          Calculated Duct Supplies ({suppliesCountLabel})
        </button>
        {selectedModeKey && (
          <span className='text-xs font-semibold text-gray-500'>
            {ZONE_DUCT_MODE_LABELS[selectedModeKey]}
          </span>
        )}
      </div>
      {quoteNotice && (
        <span className='text-xs font-semibold text-amber-600'>
          {quoteNotice}
        </span>
      )}
      {notQuotedCount > 0 && (
        <span className='text-xs text-amber-600'>{DUCT_NOT_QUOTED_NOTICE}</span>
      )}
      {missingComponentCount > 0 && (
        <span className='text-xs text-amber-600'>{DUCT_SOURCING_NOTICE}</span>
      )}
      {suppliesExpanded && (
        <div className='flex flex-col gap-2'>
          {supplies.supplyRuns.map((run) => (
            <DuctRunSuppliesGroup key={run.key} run={run} />
          ))}
          {supplies.returnRuns.map((run) => (
            <DuctRunSuppliesGroup key={run.key} run={run} />
          ))}
        </div>
      )}
    </div>
  );
}
